import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Sample } from '../../services/sample.service';
import {
  buildCaseManagerHref,
  formatSampleStorageLabels,
  sampleToCaseLocationCell,
} from '../../utils/caseLocationUtils';
import type { SampleRouterNavigationState } from '../../utils/samplePageNavigation';
import {
  formatCaseManagerOpenLabel,
  isUnassignedStorageCell,
  resolveCaseManagerOpenVerbs,
} from '../../utils/sampleStorageLinkHtml';
import { softBreakIdentifier } from '../../utils/softBreakIdentifier';

type Props = { sample: Sample };

/** 詳細の保管庫欄。未設定は一覧と同じ警告色＋ケース管理への導線（該当セルを選択） */
export default function SampleDetailStorageCell({ sample }: Props) {
  const { t } = useTranslation();
  const cell = sampleToCaseLocationCell(sample);
  const room =
    sample.storageRoom && typeof sample.storageRoom === 'object'
      ? (sample.storageRoom as { name?: string; location?: string | null })
      : sample.storageRoom ?? null;
  const { full, roomLine } = formatSampleStorageLabels(cell, room, t);
  const unassigned = isUnassignedStorageCell(cell);
  const { full: openVerbFull, short: openVerbShort } = resolveCaseManagerOpenVerbs(
    t,
    unassigned
  );
  const label = unassigned ? t('sample.case_manager.unassigned_room') : null;
  // 可視＝保管庫名。aria も短名（未設定時のみ「設定 · …」）。title＝ケース管理で開く＋経路
  // （「ケース · Freezer-A」だとケース名と誤読されるため、確定保管庫では動詞を aria に載せない）
  const aria = unassigned
    ? formatCaseManagerOpenLabel(openVerbShort, label!)
    : roomLine;
  const title = formatCaseManagerOpenLabel(openVerbFull, label ?? full);
  const linkState = {
    caseManagerPendingCell: cell,
  } satisfies SampleRouterNavigationState;
  const location = sample.storageRoom?.location?.trim();

  return (
    <Link
      to={buildCaseManagerHref(cell)}
      state={linkState}
      className={[
        'sample-storage-link',
        'sample-detail-storage-link',
        'text-decoration-none',
        unassigned ? 'sample-storage-link--unassigned' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={title}
      aria-label={aria}
      onClick={(e) => e.stopPropagation()}
    >
      <i className="fas fa-sitemap sample-detail-storage-link-icon" aria-hidden="true" />
      <span className="sample-detail-storage-link-text">
        {unassigned ? (
          label
        ) : (
          <>
            {softBreakIdentifier(sample.storageRoom?.name || roomLine)}
            {location ? (
              <span
                className="text-muted sample-detail-storage-placement"
                title={t('storage_room.location', { defaultValue: '設置場所' })}
              >
                {' · '}
                <small>{softBreakIdentifier(location)}</small>
              </span>
            ) : null}
          </>
        )}
      </span>
    </Link>
  );
}
